'use client';

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useStore, type AppGlobalState } from '@/providers/StoreProvider';
import { useStoreContext } from '@repo/store/web';
import { createChat, getChatToken, type ScheduledUnitsType } from '@repo/api';
import {
  connectWebSocket,
  disconnectWebSocket,
  isWebSocketConnected,
  sendChatMessage,
  sendSlotAppointmentMessage,
} from '@/lib/chat/chat-actions';
import { CONTENT_TYPES, type ChatTourType, type ContentType } from '@repo/utils';
import { useAnalytics } from '@/hooks/useAnalytics';
import type { Message } from '@repo/api';
import type { ListingUrgency } from '@/lib/listings/urgency';

interface UseChatSessionOptions {
  /** Provided when the chat already exists (MessageWindow) */
  chatId?: string | null;
  unitSlug?: string | null;
  /**
   * When true, the session will create a new chat via `createChat` if one
   * doesn't exist yet (ChatMessenger / listing-page overlay).
   */
  createIfMissing?: boolean;
  /**
   * The call to action that opened the chat, sent on creation so the backend can
   * open with a matching script. Only read alongside `createIfMissing`.
   */
  tourType?: ChatTourType;
  /**
   * Gate to start the session. Pass `open` from ChatMessenger so the hook
   * only runs while the overlay is visible. MessageWindow always passes true.
   */
  enabled?: boolean;
  /** Analytics context for slot selection (chat_source + urgency fields) */
  listingContext?: {
    isFutureListing?: boolean | undefined;
    isVerified?: boolean | undefined;
    urgency?: ListingUrgency | undefined;
  };
}

export interface ChatSessionResult {
  sortedMessages: Message[];
  inputValue: string;
  setInputValue: (v: string) => void;
  handleSendMessage: () => Promise<void>;
  handleSlotSelect: (value: string, message: Message) => Promise<void>;
  handleRetryMessage: (message: Message) => Promise<void>;
  wsStatus: string;
  isPolling: boolean;
  isInitializing: boolean;
  chatToken: string | null;
  chatDetailData: ReturnType<typeof useStore<any>> | null;
  resolvedChatId: string | null;
  shouldAutoScroll: boolean;
  setShouldAutoScroll: (v: boolean) => void;
  userSentMessageRef: React.MutableRefObject<boolean>;
  fetchPaginatedChat: boolean;
  loadNextPage: () => void;
  totalMessageCount: number;
  wsConnectedChatId: string | null;
}

export function useChatSession({
  chatId: propChatId,
  unitSlug,
  createIfMissing = false,
  tourType,
  enabled = true,
  listingContext,
}: UseChatSessionOptions): ChatSessionResult {
  const analytics = useAnalytics();
  const store = useStoreContext();
  const chatSlice = useStore(s => s.chat);

  const [inputValue, setInputValue] = useState('');
  const [resolvedChatId, setResolvedChatId] = useState<string | null>(propChatId ?? null);
  const [chatToken, setChatToken] = useState<string | null>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [fetchPaginatedChat, setFetchPaginatedChat] = useState(false);

  const hasInitializedRef = useRef(false);
  const isConnectingRef = useRef(false);
  const userSentMessageRef = useRef(false);

  const wsStatus = chatSlice.wsStatus;
  const isPolling = chatSlice.isPolling;
  const chatDetailData = chatSlice.chatDetailData;
  const wsConnectedChatId = chatSlice.wsConnectedChatId;

  const messages = useMemo(() => chatSlice.chatDetail || [], [chatSlice.chatDetail]);

  const sortedMessages = useMemo(
    () =>
      [...messages].sort(
        (a, b) => new Date(a.atCreated).getTime() - new Date(b.atCreated).getTime(),
      ),
    [messages],
  );

  const totalMessageCount = chatDetailData?.chatMessages?.count ?? 0;

  // Sync propChatId changes (MessageWindow navigating between chats).
  // Reset chat detail so stale messages from the previous chat are cleared immediately,
  // reset polling so the loading bubble doesn't bleed in, and reset userSentMessageRef
  // so the new chat's initial scroll-to-bottom isn't suppressed.
  useEffect(() => {
    if (propChatId) {
      chatSlice.resetChatDetail();
      setResolvedChatId(propChatId);
      chatSlice.setPollingStatus(false);
      userSentMessageRef.current = false;
    }
  }, [propChatId]);

  // Reset pagination when chat changes
  useEffect(() => {
    if (resolvedChatId) {
      setCurrentPage(1);
      setShouldAutoScroll(true);
    }
  }, [resolvedChatId]);

  // ── Initialisation: fetch token + create chat (if needed) + fetch messages ──
  useEffect(() => {
    if (!enabled || !unitSlug) {
      hasInitializedRef.current = false;
      setChatToken(null);
      return;
    }

    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const init = async () => {
      try {
        const { token } = await getChatToken();
        if (!token) throw new Error('No chat token');
        setChatToken(token);

        let activeChatId = propChatId ?? null;

        if (createIfMissing && !activeChatId) {
          const currentUnits = store.getState().chat.scheduledUnits;
          const existing = currentUnits.find((u: ScheduledUnitsType) => u?.unit?.slug === unitSlug);
          activeChatId = existing ? existing.id : (await createChat(unitSlug, tourType)).chat.id;
          setResolvedChatId(activeChatId);
        }

        if (!activeChatId) return;

        // Skip fetch if we already have matching data
        const existingDetail = store.getState().chat.chatDetailData;
        if (
          existingDetail?.chat?.id === activeChatId &&
          (existingDetail.chatMessages?.results?.length ?? 0) > 0
        ) {
          return;
        }

        await chatSlice.fetchChatDetail(activeChatId, 1);
      } catch {
        hasInitializedRef.current = false;
        setChatToken(null);
      }
    };

    void init();

    return () => {
      if (createIfMissing) {
        // Overlay: full reset on close
        chatSlice.resetChatDetail();
        setResolvedChatId(null);
        setChatToken(null);
        hasInitializedRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, unitSlug]);

  // ── WebSocket connection ──
  const chatIdFromData = chatSlice.chatDetailData?.chat?.id;
  useEffect(() => {
    if (!enabled || !resolvedChatId || !unitSlug || !chatToken || !chatIdFromData) {
      isConnectingRef.current = false;
      return;
    }

    // Store still has stale data from a previous chat — wait for fetchChatDetail to complete
    // before connecting, otherwise the cleanup from this effect run would tear down the
    // connection that the NEXT run (with correct chatIdFromData) just established.
    if (chatIdFromData !== resolvedChatId) {
      isConnectingRef.current = false;
      return;
    }

    if (isConnectingRef.current) return;

    // Tracks whether this effect run actually kicked off a connect, so cleanup
    // can tear the socket down even if it closed before the store recorded
    // 'connected' (closing mid-connect would otherwise orphan the socket and
    // leave a stale 'connected' status that blocks the next open).
    let didStartConnect = false;

    const connect = async () => {
      const s = store.getState() as unknown as AppGlobalState;
      const { wsConnectedChatId: connId, wsStatus: connStatus } = s.chat;
      // Only skip when a connect is genuinely in progress, or the live socket
      // is really open for this chat. Relying on the store status alone let a
      // stale 'connected' value (e.g. from a previously closed overlay) block
      // the reconnect, leaving the user unable to send messages.
      if (connId === resolvedChatId) {
        if (connStatus === 'connecting') return;
        if (connStatus === 'connected' && isWebSocketConnected()) return;
      }

      isConnectingRef.current = true;
      didStartConnect = true;
      try {
        await connectWebSocket(
          resolvedChatId,
          unitSlug,
          chatToken,
          () => store.getState() as unknown as AppGlobalState,
        );
      } catch {
        isConnectingRef.current = false;
      }
    };

    void connect();

    return () => {
      isConnectingRef.current = false;
      const s = store.getState() as unknown as AppGlobalState;
      if (didStartConnect || s.chat.wsConnectedChatId === resolvedChatId) disconnectWebSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatIdFromData, resolvedChatId, unitSlug, enabled, chatToken]);

  // ── Handlers ──
  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || !resolvedChatId || !unitSlug || wsStatus !== 'connected') return;

    const messageText = inputValue.trim();
    analytics.track('message_sent', { message: messageText, messageLength: messageText.length });
    userSentMessageRef.current = true;
    setShouldAutoScroll(true);
    setInputValue('');

    try {
      await sendChatMessage(
        {
          chatId: resolvedChatId,
          unitSlug,
          contentType: CONTENT_TYPES.TEXT,
          contentInput: messageText,
        },
        () => store.getState() as unknown as AppGlobalState,
      );
    } catch {
      analytics.track('message_failed_to_send');
    }
  }, [analytics, inputValue, resolvedChatId, unitSlug, wsStatus, store]);

  const handleSlotSelect = useCallback(
    async (value: string, message: Message) => {
      if (!resolvedChatId || !unitSlug) return;
      try {
        analytics.track('message_slot_select', {
          unitId: unitSlug,
          date: value,
          chat_source: listingContext?.isFutureListing
            ? 'future'
            : listingContext?.isVerified
              ? 'current_verified'
              : 'current_non_verified',
          ...(listingContext?.urgency ? { urgency: listingContext.urgency } : {}),
        });
        await sendSlotAppointmentMessage(
          { chatId: resolvedChatId, unitSlug, selectedValue: value, relatedBotMessage: message },
          () => store.getState() as unknown as AppGlobalState,
        );
      } catch {}
    },
    [analytics, resolvedChatId, unitSlug, store, listingContext],
  );

  const handleRetryMessage = useCallback(
    async (message: Message) => {
      if (!resolvedChatId || !unitSlug) return;
      try {
        chatSlice.removeChatMessageById(message.id);
        await sendChatMessage(
          {
            chatId: resolvedChatId,
            unitSlug,
            contentType: message.contentType as ContentType,
            contentInput: message.contentMessage || message.contentInput || '',
          },
          () => store.getState() as unknown as AppGlobalState,
        );
      } catch (e) {
        console.error('Failed to retry message:', e);
      }
    },
    [resolvedChatId, unitSlug, chatSlice, store],
  );

  const loadNextPage = useCallback(() => {
    if (!resolvedChatId || fetchPaginatedChat) return;
    if (totalMessageCount <= messages.length) return;
    setFetchPaginatedChat(true);
    const nextPage = currentPage + 1;
    void chatSlice.fetchChatDetail(resolvedChatId, nextPage).then(() => {
      setCurrentPage(nextPage);
      setFetchPaginatedChat(false);
    });
  }, [
    resolvedChatId,
    fetchPaginatedChat,
    totalMessageCount,
    messages.length,
    currentPage,
    chatSlice,
  ]);

  const isInitializing = enabled && (!chatToken || !resolvedChatId || !chatDetailData);

  return {
    sortedMessages,
    inputValue,
    setInputValue,
    handleSendMessage,
    handleSlotSelect,
    handleRetryMessage,
    wsStatus,
    isPolling,
    isInitializing,
    chatToken,
    chatDetailData,
    resolvedChatId,
    shouldAutoScroll,
    setShouldAutoScroll,
    userSentMessageRef,
    fetchPaginatedChat,
    loadNextPage,
    totalMessageCount,
    wsConnectedChatId,
  };
}
